/** @jsxImportSource @emotion/react */
import React, { FC, useEffect } from "react";
import { toast } from "sonner";
import { addDays } from "date-fns";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { DateTime } from "luxon";
import { useActiveStoreTimezone } from "src/modules/hooks/use-store-timezone";

import { Spinner } from "src/@/components/ui/spinner";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "src/@/components/ui/dialog";
import { TimeInput } from "#dashboard/components/pause-orders/pause-orders-modal/time-input";
import { Button } from "src/@/components/ui/button";
import { Separator } from "src/@/components/ui/separator";
import { DateTimePicker } from "src/@/components/ui/date-time-picker";

import {
    Action,
    PauseReason,
    PauseTime,
    useToggleOrders,
} from "../use-toggle-orders";

import { ReasonInput } from "./reason-input";

interface ModalProps {
    showModal: boolean;
    setShowModal: (e: boolean) => void;
}

export const PauseOrdersModal: FC<ModalProps> = ({
    showModal,
    setShowModal,
}) => {
    const {
        isLoading,
        resetFields,
        selectedTime,
        setSelectedTime,
        selectedDate,
        setSelectedDate,
        selectedReason,
        setSelectedReason,
        toggleOrders,
        closedUntil,
        customReason,
        setCustomReason,
    } = useToggleOrders();

    const storeTimezone = useActiveStoreTimezone();

    const onConfirm = async () => {
        if (isLoading) return;
        if (selectedReason === PauseReason.Other && customReason.length < 1) {
            toast.error(t`Please enter a custom reason`);
            return;
        }

        if (selectedTime === PauseTime.Custom && selectedDate) {
            const now = DateTime.now().setZone(storeTimezone);
            const selectedDateTime = DateTime.fromJSDate(selectedDate).setZone(storeTimezone);
            
            if (selectedDateTime <= now) {
                toast.error(t`Please select a future date and time`);
                return;
            }
        }

        await toggleOrders({ action: Action.Pause });
        resetFields();
        setShowModal(false);
    };

    useEffect(() => {
        if (closedUntil) {
            toast.success(t`App and Online orders paused until ${closedUntil}`, {
                description: t`If desired, please manually refund any orders scheduled for this period.`,
            });
        }
    }, [closedUntil]);
    return (
        <>
            <Dialog onOpenChange={setShowModal} open={showModal}>
                <DialogContent className="min-w-min">
                    <DialogHeader>
                        <DialogTitle><Trans>Pause Online Orders</Trans></DialogTitle>
                        <DialogDescription>
                            <Trans>Stop new App and Online Orders from coming in for a set amount of time. In-Person Orders won't be affected.</Trans>
                        </DialogDescription>
                    </DialogHeader>

                    <Separator />
                    <div className="flex max-h-80 min-w-min flex-col space-y-4 overflow-y-auto p-1 lg:max-h-[50vh]">
                        <TimeInput
                            selectedOption={selectedTime}
                            selectOption={setSelectedTime}
                        />

                        <div className="flex w-full flex-col items-stretch justify-between lg:flex-row lg:space-x-4">
                            {selectedTime === PauseTime.Custom ? (
                                <div className="flex flex-col">
                                    <h6 className="my-4"><Trans>Pause orders until</Trans></h6>
                                    <p className="text-sm text-muted-foreground mb-4">
                                        <Trans>Select time in your local timezone</Trans>
                                    </p>
                                    <DateTimePicker
                                        date={selectedDate}
                                        setDate={setSelectedDate}
                                        className="self-center"
                                        disabledDate={(current: Date) => {
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            const maxDate = addDays(new Date(), 30);
                                            return current < today || current > maxDate;
                                        }}
                                        showTime={true}
                                    />
                                    {selectedDate && (
                                        <div className="mt-4 p-3 bg-muted/50 rounded-lg border border-border">
                                            <div className="flex items-center gap-2 text-sm">
                                                <span className="text-muted-foreground">
                                                    <Trans>Local Store Time</Trans>
                                                </span>
                                                <span className="font-medium text-foreground">
                                                    {DateTime.fromJSDate(selectedDate).setZone(storeTimezone).toFormat('MMM d, h:mm a')}
                                                </span>
                                                <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-md">
                                                    {DateTime.fromJSDate(selectedDate).setZone(storeTimezone).toFormat('ZZZZ')}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : null}
                            <ReasonInput
                                options={PauseReason}
                                selectedOption={selectedReason}
                                selectOption={setSelectedReason}
                                customReason={customReason}
                                setCustomReason={setCustomReason}
                            />
                        </div>
                    </div>
                    <Separator className="mt-3" />
                    <DialogFooter>
                        <div className="flex w-full justify-end space-x-2">
                            <Button
                                variant="ghost"
                                onClick={() => setShowModal(false)}
                            >
                                <Trans>Cancel</Trans>
                            </Button>
                            <Button
                                onClick={onConfirm}
                                disabled={
                                    !selectedReason ||
                                    (selectedReason === PauseReason.Other &&
                                        customReason.length === 0) ||
                                    isLoading
                                }
                            >
                                {isLoading ? <Spinner /> : <Trans>Confirm</Trans>}
                            </Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
